import { getApiBaseUrl } from '../config/api';

export interface Appointment {
  id: string;
  patientId: string;
  caretakerId: string;
  serviceId: string;
  scheduledAt: string;
  duration: number;
  status: 'REQUESTED' | 'SCHEDULED' | 'CONFIRMED' | 'IN_PROGRESS' | 'COMPLETED' | 'CANCELLED' | 'NO_SHOW';
  serviceAddress?: string | null;
  notes?: string;
  specialRequirements?: string;
  totalAmount?: number;
  paymentStatus: 'PENDING' | 'PAID' | 'FAILED' | 'REFUNDED' | 'PARTIALLY_REFUNDED';
  paymentId?: string;
  createdAt: string;
  updatedAt: string;
  patient?: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
    phoneNumber?: string;
  };
  caretaker?: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
    phoneNumber?: string;
    avatar?: string;
  };
  service?: {
    id: string;
    name: string;
    description?: string;
    duration?: number;
    price?: number;
  };
}

export interface TimeSlot {
  id: string;
  startTime: string;
  endTime: string;
  isAvailable: boolean;
}

export interface BookAppointmentData {
  caretakerId: string;
  serviceId: string;
  scheduledAt: string;
  notes?: string;
  specialRequirements?: string;
  serviceAddress?: string;
}

export interface UpdateAppointmentData {
  scheduledAt?: string;
  notes?: string;
  specialRequirements?: string;
  status?: 'REQUESTED' | 'SCHEDULED' | 'CONFIRMED' | 'CANCELLED';
}

class AppointmentService {
  private createIdempotencyKey(prefix: string): string {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      return `${prefix}-${crypto.randomUUID()}`;
    }

    return `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2)}`;
  }

  private getAuthHeaders(): HeadersInit {
    const token = localStorage.getItem('authToken');
    return {
      'Content-Type': 'application/json',
      ...(token && { Authorization: `Bearer ${token}` }),
    };
  }

  async bookAppointment(data: BookAppointmentData): Promise<Appointment> {
    const baseUrl = await getApiBaseUrl();
    const response = await fetch(`${baseUrl}/appointments/book`, {
      method: 'POST',
      headers: {
        ...this.getAuthHeaders(),
        'Idempotency-Key': this.createIdempotencyKey('web-appointment-book'),
      },
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || error.error || 'Failed to book appointment');
    }

    const result = await response.json();
    return result.data;
  }

  async getMyAppointments(): Promise<Appointment[]> {
    const baseUrl = await getApiBaseUrl();
    const response = await fetch(`${baseUrl}/appointments/my-appointments`, {
      method: 'GET',
      headers: this.getAuthHeaders(),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to fetch appointments');
    }

    const result = await response.json();
    return result.data || [];
  }

  async getAppointment(appointmentId: string): Promise<Appointment> {
    const baseUrl = await getApiBaseUrl();
    const response = await fetch(`${baseUrl}/appointments/${appointmentId}`, {
      method: 'GET',
      headers: this.getAuthHeaders(),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to fetch appointment');
    }

    const result = await response.json();
    return result.data;
  }

  async updateAppointment(appointmentId: string, data: UpdateAppointmentData): Promise<Appointment> {
    const baseUrl = await getApiBaseUrl();
    const response = await fetch(`${baseUrl}/appointments/${appointmentId}`, {
      method: 'PUT',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to update appointment');
    }

    const result = await response.json();
    return result.data;
  }

  async cancelAppointment(appointmentId: string): Promise<void> {
    const baseUrl = await getApiBaseUrl();
    const response = await fetch(`${baseUrl}/appointments/${appointmentId}`, {
      method: 'DELETE',
      headers: this.getAuthHeaders(),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to cancel appointment');
    }
  }

  async getAvailableSlots(caretakerId: string, date: string): Promise<{ availableSlots: TimeSlot[] }> {
    const baseUrl = await getApiBaseUrl();
    const response = await fetch(
      `${baseUrl}/time-slots/available?caretakerId=${caretakerId}&date=${date}`,
      {
        method: 'GET',
        headers: this.getAuthHeaders(),
      }
    );

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to fetch available slots');
    }

    const result = await response.json();
    return { availableSlots: result.data || [] };
  }

  async createTimeSlot(data: {
    date: string;
    startTime: string;
    endTime: string;
    isAvailable?: boolean;
  }): Promise<TimeSlot> {
    const baseUrl = await getApiBaseUrl();
    const response = await fetch(`${baseUrl}/time-slots`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to create time slot');
    }

    const result = await response.json();
    return result.data;
  }

  async createBulkTimeSlots(data: {
    date: string;
    timeSlots: Array<{
      startTime: string;
      endTime: string;
      isAvailable?: boolean;
    }>;
  }): Promise<{ count: number }> {
    const baseUrl = await getApiBaseUrl();
    const response = await fetch(`${baseUrl}/time-slots/bulk`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to create time slots');
    }

    const result = await response.json();
    return result.data || { count: 0 };
  }

  async getMyTimeSlots(startDate?: string, endDate?: string): Promise<TimeSlot[]> {
    const params = new URLSearchParams();
    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);

    const baseUrl = await getApiBaseUrl();
    const response = await fetch(
      `${baseUrl}/time-slots/my-slots?${params.toString()}`,
      {
        method: 'GET',
        headers: this.getAuthHeaders(),
      }
    );

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to fetch time slots');
    }

    const result = await response.json();
    return result.data || [];
  }

  async updateTimeSlot(timeSlotId: string, isAvailable: boolean): Promise<TimeSlot> {
    const baseUrl = await getApiBaseUrl();
    const response = await fetch(`${baseUrl}/time-slots/${timeSlotId}`, {
      method: 'PUT',
      headers: this.getAuthHeaders(),
      body: JSON.stringify({ isAvailable }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to update time slot');
    }

    const result = await response.json();
    return result.data;
  }

  async deleteTimeSlot(timeSlotId: string): Promise<void> {
    const baseUrl = await getApiBaseUrl();
    const response = await fetch(`${baseUrl}/time-slots/${timeSlotId}`, {
      method: 'DELETE',
      headers: this.getAuthHeaders(),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to delete time slot');
    }
  }
}

export const appointmentService = new AppointmentService();
